import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import rapprochementAPI from '../../services/rapprochementAPI';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import FilterInput from '../../components/ui/FilterInput';

const HEADER_HEIGHT = 76;
const SIDEBAR_WIDTH = '340px';

const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  background: '#FFFFFF',
  borderRadius: '12px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  padding: '10px 12px',
  marginBottom: '6px',
  gap: '10px',
};

const COMMERCIAL_ROW_STYLE = {
  ...ROW_STYLE,
  padding: '6px 12px',
};

const SIDEBAR_ROW = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '9px 0',
};

const SIDEBAR_LABEL = {
  fontFamily: 'Montserrat',
  fontSize: '12px',
  fontWeight: 500,
  color: '#000000',
  opacity: 0.88,
};

const SIDEBAR_HEADER_LABEL = {
  fontFamily: 'Montserrat',
  fontSize: '12px',
  fontWeight: 600,
  color: '#919191',
};

const SIDEBAR_VALUE = {
  fontFamily: 'Montserrat',
  fontSize: '13px',
  fontWeight: 600,
  color: '#38485C',
};

const ROW_DATE_STYLE = {
  fontFamily: 'Montserrat',
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: '#75808E',
};

const CATEGORY_TEXT_STYLE = {
  fontFamily: 'Montserrat',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 600,
  color: '#38485C',
  textTransform: 'capitalize',
  wordBreak: 'break-word',
};

const BC_TEXT_STYLE = {
  fontFamily: 'Montserrat',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 600,
  color: '#027BC6',
};

const ROW_ID_STYLE = {
  fontFamily: 'Montserrat',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 600,
  color: '#38485C',
  opacity: 0.88,
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()} - ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
};

const formatSigned = (val) => {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  return formatAmountForDisplay(n, n < 0).text;
};

const ecartColor = (val) => {
  if (val === null || val === undefined) return '#38485C';
  return val > 0 ? '#0AD69F' : val < 0 ? '#EF456E' : '#919191';
};

const RapprochementDetail = () => {
  const { id } = useParams();

  const [rapprochement, setRapprochement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [commercialSearch, setCommercialSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [verificationSearch, setVerificationSearch] = useState('');

  useEffect(() => {
    rapprochementAPI.getById(id)
      .then(res => setRapprochement(res.data))
      .catch(() => setError('Échec du chargement du rapprochement'))
      .finally(() => setLoading(false));
  }, [id]);

  const commercialTxs = useMemo(() =>
    (rapprochement?.transactions || []).filter(t => t.side === 'COMMERCIAL')
  , [rapprochement]);

  const verificationTxs = useMemo(() =>
    (rapprochement?.transactions || []).filter(t => t.side === 'VERIFICATION')
  , [rapprochement]);

  const availableCategories = useMemo(() =>
    [...new Set(commercialTxs.map(t => t.categoryLabel).filter(Boolean))].sort()
  , [commercialTxs]);

  const filteredCommercialTxs = useMemo(() => {
    return commercialTxs.filter(t => {
      if (selectedCategories.length > 0 && !selectedCategories.includes(t.categoryLabel)) return false;
      if (!commercialSearch) return true;
      const term = commercialSearch.toLowerCase();
      const nt = term.replace(/[\s,]/g, '');
      return (
        (t.code || '').toLowerCase().includes(term) ||
        (t.categoryLabel || '').toLowerCase().includes(term) ||
        (t.bcNumber || '').toLowerCase().includes(term) ||
        String(t.amount ?? '').includes(nt)
      );
    });
  }, [commercialTxs, commercialSearch, selectedCategories]);

  const filteredVerificationTxs = useMemo(() => {
    return verificationTxs.filter(t => {
      if (!verificationSearch) return true;
      const term = verificationSearch.toLowerCase();
      const nt = term.replace(/[\s,]/g, '');
      return (
        (t.code || '').toLowerCase().includes(term) ||
        (t.bcNumber || '').toLowerCase().includes(term) ||
        String(t.amount ?? '').includes(nt)
      );
    });
  }, [verificationTxs, verificationSearch]);

  const versementAmount = Number(rapprochement?.versementAmount) || 0;
  const transactionsNet = Number(rapprochement?.transactionsNet) || 0;
  const verificationsTotal = Number(rapprochement?.verificationsTotal) || 0;
  const transactionsSales = useMemo(() =>
    commercialTxs.filter(t => Number(t.caisseFactor) >= 1)
      .reduce((s, t) => s + (Number(t.amount) || 0), 0)
  , [commercialTxs]);

  const ecartVT = commercialTxs.length > 0 ? versementAmount - transactionsNet : null;
  const ecartTV = commercialTxs.length > 0 && verificationTxs.length > 0
    ? transactionsSales - verificationsTotal : null;

  const soldeLorsVersement = Number(rapprochement?.soldeLorsVersement) || 0;
  const caisseName = rapprochement?.caisseName || '';
  const versementCode = rapprochement?.versementCode || '';
  const versementDate = rapprochement?.versementDate ? formatDate(rapprochement.versementDate) : '';
  const createdByName = rapprochement?.createdBy
    ? `${rapprochement.createdBy.firstName || ''} ${rapprochement.createdBy.lastName || ''}`.trim()
    : '';

  const adjustmentAmount = rapprochement?.adjustmentAmount;
  const adjustmentDirection = rapprochement?.adjustmentDirection;
  const adjustmentNote = rapprochement?.adjustmentNote;

  const adjustmentDisplay = adjustmentAmount && Number(adjustmentAmount) > 0 ? (() => {
    const isDeduct = adjustmentDirection === 'DEDUCT';
    const val = isDeduct ? -Number(adjustmentAmount) : Number(adjustmentAmount);
    return {
      text: formatAmountForDisplay(Math.abs(val), false).text,
      color: isDeduct ? '#EF456E' : '#0AD69F',
    };
  })() : null;

  const categoryOptions = availableCategories.map(c => ({ value: c, label: c }));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${HEADER_HEIGHT}px)`, overflow: 'hidden', background: '#F8F8F8' }}>

      {/* Page header */}
      <Box sx={{ flexShrink: 0, background: 'linear-gradient(to bottom, #fff, #F8F8F8)', px: { xs: '26px', sm: '42px' }, pt: '24px', pb: '16px' }}>
        <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', color: '#919191', margin: '0 0 4px 0' }}>
          Détails du rapprochement
        </p>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '4px', flexWrap: 'wrap' }}>
          <h1 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '24px', lineHeight: '28px', color: '#027BC6', opacity: 1, margin: 0 }}>
            {versementCode}
          </h1>
          <Box sx={{ width: '64px', height: '26px', background: '#027BC6', borderRadius: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontFamily: 'Montserrat', fontSize: '14px', lineHeight: '18px', fontWeight: 600, color: '#FFFFFF', textTransform: 'capitalize' }}>Traité</span>
          </Box>
        </Box>
        <p style={{ fontFamily: 'Montserrat', fontSize: '20px', lineHeight: '28px', fontWeight: 600, color: '#75808E', margin: 0, textTransform: 'capitalize' }}>
          {rapprochement?.createdAt ? formatDate(rapprochement.createdAt) : ''}
        </p>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
      </Box>

      {/* Main 3-column layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', gap: '16px', px: { xs: '26px', sm: '42px' }, pb: '16px' }}>

        {/* LEFT PANEL — Commercial transactions */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, pb: '12px' }}>
            <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', color: '#38485C', margin: '0 0 12px 0' }}>
              Liste des transactions
            </p>
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <FilterInput type="search" value={commercialSearch} onChange={setCommercialSearch} onClear={() => setCommercialSearch('')} hasValue={!!commercialSearch} />
              <Box sx={{ width: '200px', flexShrink: 0 }}>
                <FilterInput type="dropdown" label="Catégorie" name="selectedCategories" options={categoryOptions} value={selectedCategories} onChange={setSelectedCategories} onClear={() => setSelectedCategories([])} isMultiSelect={true} hasValue={selectedCategories.length > 0} placeholder="Toutes" />
              </Box>
            </Box>
          </Box>

          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: '12px', pb: '6px', gap: '10px' }}>
            <Box sx={{ flex: 1, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Transaction ID</Box>
            <Box sx={{ flex: 1, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Catégorie / N° BC</Box>
            <Box sx={{ width: '130px', flexShrink: 0, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Montant</Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredCommercialTxs.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 4, fontFamily: 'Montserrat', fontSize: '14px', color: '#919191' }}>
                Aucune transaction
              </Box>
            ) : filteredCommercialTxs.map(tx => {
              const signed = (Number(tx.amount) || 0) * (Number(tx.caisseFactor) || 1);
              const amountColor = signed > 0 ? '#0AD69F' : signed < 0 ? '#EF456E' : '#919191';
              const amountText = formatAmountForDisplay(signed, signed < 0).text;
              return (
                <Box key={tx.transactionId} sx={{ ...COMMERCIAL_ROW_STYLE }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <div style={ROW_ID_STYLE}>{tx.code}</div>
                    <div style={ROW_DATE_STYLE}>{formatDate(tx.createdAt)}</div>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <div style={CATEGORY_TEXT_STYLE}>{tx.categoryLabel}</div>
                    {tx.bcNumber && (
                      <div style={BC_TEXT_STYLE}>BC N°{tx.bcNumber}</div>
                    )}
                  </Box>
                  <Box sx={{ width: '130px', flexShrink: 0 }}>
                    <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 700, color: amountColor }}>{amountText}</span>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* RIGHT PANEL — Verifications */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, pb: '12px' }}>
            <p style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '16px', color: '#38485C', margin: '0 0 12px 0' }}>
              Liste des vérifications
            </p>
            <FilterInput type="search" value={verificationSearch} onChange={setVerificationSearch} onClear={() => setVerificationSearch('')} hasValue={!!verificationSearch} />
          </Box>

          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', px: '12px', pb: '6px', gap: '10px' }}>
            <Box sx={{ flex: 1, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Vérification ID</Box>
            <Box sx={{ flex: 1, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Bon de caisse</Box>
            <Box sx={{ width: '130px', flexShrink: 0, fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#919191' }}>Montant</Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredVerificationTxs.length === 0 ? (
              <Box sx={{ textAlign: 'center', pt: 4, fontFamily: 'Montserrat', fontSize: '14px', color: '#919191' }}>
                Aucune vérification
              </Box>
            ) : filteredVerificationTxs.map(tx => (
              <Box key={tx.transactionId} sx={{ ...COMMERCIAL_ROW_STYLE }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <div style={ROW_ID_STYLE}>{tx.code}</div>
                  <div style={ROW_DATE_STYLE}>{formatDate(tx.createdAt)}</div>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {tx.bcNumber ? (
                    <div style={BC_TEXT_STYLE}>BC N°{tx.bcNumber}</div>
                  ) : (
                    <div style={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#919191', fontStyle: 'italic' }}>—</div>
                  )}
                </Box>
                <Box sx={{ width: '130px', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 700, color: '#027BC6' }}>
                    {formatAmountForDisplay(tx.amount, false).text}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* SIDEBAR */}
        <Box sx={{
          width: SIDEBAR_WIDTH, flexShrink: 0, display: 'flex', flexDirection: 'column',
          alignSelf: 'flex-start',
          maxHeight: '100%',
          background: '#FFFFFF', borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          {/* Header block */}
          <Box sx={{ background: '#E9F5FD', px: '12px', pt: '12px', pb: '12px', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '10px' }}>
              <Box>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Caisse</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#000000', opacity: 0.88, wordBreak: 'break-word', maxWidth: '160px' }}>{caisseName}</div>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Versement ID</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#027BC6' }}>{versementCode}</div>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Date</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#000000' }}>{versementDate}</div>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Montserrat', fontSize: '10px', fontWeight: 600, color: '#919191', marginBottom: '2px' }}>Solde lors du versement</div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 600, color: '#027BC6' }}>
                  {formatAmountForDisplay(soldeLorsVersement, false).text}
                </div>
              </Box>
            </Box>
          </Box>

          <Box sx={{ height: '1px', background: '#E3E3E3', flexShrink: 0 }} />

          {/* Scrollable content */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '12px', gap: '4px' }}>
            <Box sx={{ px: '4px' }}>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Versement sélectionné</span>
                <span style={{ ...SIDEBAR_VALUE, color: '#027BC6' }}>{formatAmountForDisplay(versementAmount, false).text}</span>
              </Box>

              <Box sx={{ borderTop: '1px dashed #E3E3E3', mx: '4px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Transactions ({commercialTxs.length})</span>
                <span style={SIDEBAR_VALUE}>{formatSigned(transactionsNet)}</span>
              </Box>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Écart versement/transactions</span>
                <span style={{ ...SIDEBAR_VALUE, color: ecartColor(ecartVT) }}>
                  {ecartVT !== null ? formatSigned(ecartVT) : '—'}
                </span>
              </Box>

              <Box sx={{ height: '1px', background: '#E3E3E3', mx: '-16px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Transactions sélectionnées ({commercialTxs.length})</span>
                <span style={SIDEBAR_VALUE}>{formatAmountForDisplay(transactionsSales, false).text}</span>
              </Box>

              <Box sx={{ borderTop: '1px dashed #E3E3E3', mx: '4px' }} />

              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Vérifications ({verificationTxs.length})</span>
                <span style={SIDEBAR_VALUE}>{formatAmountForDisplay(verificationsTotal, false).text}</span>
              </Box>
              <Box sx={SIDEBAR_ROW}>
                <span style={SIDEBAR_LABEL}>Écart transactions/vérification</span>
                <span style={{ ...SIDEBAR_VALUE, color: ecartColor(ecartTV) }}>
                  {ecartTV !== null ? formatSigned(ecartTV) : '—'}
                </span>
              </Box>
            </Box>

            {/* Adjustment section */}
            {(adjustmentDisplay || adjustmentNote) && (
              <>
                <Box sx={{ height: '1px', background: '#E3E3E3', mx: '-12px', my: '4px' }} />
                <Box sx={{ px: '4px' }}>
                  {adjustmentDisplay && (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '4px' }}>
                        <span style={SIDEBAR_HEADER_LABEL}>Par</span>
                        <span style={SIDEBAR_HEADER_LABEL}>Montant Ajustement</span>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '2px', mb: '8px' }}>
                        <span style={{ ...SIDEBAR_VALUE, fontSize: '12px', color: '#027BC6' }}>{createdByName}</span>
                        <span style={{ ...SIDEBAR_VALUE, color: adjustmentDisplay.color }}>{adjustmentDisplay.text}</span>
                      </Box>
                    </>
                  )}
                  {adjustmentDisplay && adjustmentNote && (
                    <Box sx={{ borderTop: '1px dashed #E3E3E3', mx: '4px' }} />
                  )}
                  {adjustmentNote && (
                    <Box sx={{ pt: '9px' }}>
                      <div style={{ ...SIDEBAR_HEADER_LABEL, marginBottom: '4px' }}>Note</div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: '#38485C', lineHeight: '1.6', wordBreak: 'break-word' }}>
                        {adjustmentNote}
                      </div>
                    </Box>
                  )}
                </Box>
              </>
            )}

            <Box sx={{ minHeight: '8px' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RapprochementDetail;
